/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  JoinStateDetails,
  PlaylistType,
  ResourceAuthorDetails,
  ResourcePermission,
  ResourcePublishedStatus,
  ResourceSubheader,
  ResourceTag,
} from '..';
import { EventCardTime } from '../../../page-modules/resource/store/admin/resource-event-admin.state.model';
import { LikeInfoDetails } from '../like-info/like-info.model';

export type ResourceType =
  | 'PLAYLIST'
  | 'CARD'
  | 'ASSESSMENT'
  | 'RESOURCE'
  | 'PERSONALITY_INDICATOR'
  | 'INSTITUTIONAL_LIBRARY'
  | 'TARGET_CONNECT'
  | 'CAREER_HUB'
  | 'WEBLINK'
  | 'EVENT'
  | 'ASSIGNMENT';

export type CardsLayoutType = 'CardView' | 'ListView' | 'NumberedListView' | 'SnakeView';

export type LmsPlatformType = 'CANVAS' | 'BLACKBOARD' | 'MOODLE' | 'BRIGHT_SPACE' | 'OPENOLAT';

export const LMS_PLATFORM_LABELS: Record<LmsPlatformType, string> = {
  CANVAS: 'Canvas',
  BLACKBOARD: 'Blackboard',
  MOODLE: 'Moodle',
  BRIGHT_SPACE: 'Brightspace',
  OPENOLAT: 'OpenOlat',
};

export interface PlaylistCardShort {
  _id: string;
  header: string;
  image: string;
  thumbnail: string;
  completed: boolean;
  started: boolean;
  uri: string;
  blurb: string;
  formattedUri: string;
  authorDetails: ResourceAuthorDetails;
  type?: ResourceType;
  collectionType?: PlaylistType;
  v3CardType?: PlaylistType;
  permission?: ResourcePermission;
  status: ResourcePublishedStatus;
  required?: boolean;
  syndicated?: boolean;
  likes: LikeInfoDetails;
  subHeader: ResourceSubheader[];
  webLinkUrl?: string;
  eventTime?: EventCardTime;
  completedItemsCount: number;
  totalItemsCount: number;
  tags: ResourceTag[];
  notes?: number;
  prerequisitesCompleted?: boolean;
  deadline?: Date;
  /** Set on playlists and featured items of a v3 playlist, in the context it was opened from. */
  joinState?: JoinStateDetails | null;
  /** ISO datetime of the last edit; drives the learner's "Most recent" sort in collections. */
  lastModifiedDate?: string;

  /** LMS integration fields */
  lmsCourseUrl?: string;
  lmsPlatformType?: LmsPlatformType;

  /** Frontend specific. */
  shouldBeCloned?: boolean;
  applyTagsFilter?: boolean;
  /**
   * Frontend specific: only set on ASSIGNMENT section items by the course
   * content tab — the state chip/badge resolved for the current viewer (the learner's own submission state
   * from the learner feed, or the admin Draft/Published/Archived badge), plus the deadline meta.
   */
  assignmentItemState?: AssignmentItemState;
}

/** The badge an ASSIGNMENT section item shows; the value set mirrors the two audiences. */
export type AssignmentItemBadge =
  /** Learner audience — the learner's own review state (`LearnerReviewStatus`). */
  | 'NOT_SUBMITTED'
  | 'UNDER_REVIEW'
  | 'RETURNED'
  | 'RELEASED'
  /** Admin audience — the card's own publication state (New/UnPublished collapse to DRAFT). */
  | 'DRAFT'
  | 'PUBLISHED'
  | 'ARCHIVED';

export interface AssignmentItemState {
  badge: AssignmentItemBadge;
  /** ISO datetime of the AT deadline; null/undefined for rolling deadlines and in the admin audience. */
  deadline?: string | null;
}

export interface RemoveCardAction {
  cardUid: string;
  type: ResourceType;
}

export interface LinkableCardSummary {
  header: string;
  subHeader?: string;
  id: string;
  organizationLogo?: string;
  status?: { type: ResourcePublishedStatus; on: Date; by: string };
  syndicated?: boolean;
  formattedUri?: string;
  thumbnail?: string;
  uri?: string;
}

export type CardType = 'RESOURCE' | 'ASSESSMENT' | 'EVENT' | 'ASSIGNMENT';

export type CardUIType = 'course' | 'event' | 'playlist' | 'general';
